import React, { useState } from "react";
import { fmt, shortSvc, extractName } from "../api";
import { useDashboard } from "../context/DashboardContext";
import { useVistaQuery } from "../hooks/useQuery";
import FilterPanel from "../components/FilterPanel";

export default function Explorer() {
  const { filters } = useDashboard();
  const [sortBy, setSortBy] = useState<"cost" | "service" | "account">("cost");

  // Resources — server-side filtered via useVistaQuery (reads filters from context)
  const { data: rawData, loading } = useVistaQuery<any[]>("/api/query/top-resources", { limit: 100 });
  const data = rawData ?? [];

  // Sort
  const sorted = [...data].sort((a, b) => {
    if (sortBy === "cost") return (parseFloat(b.cost) || 0) - (parseFloat(a.cost) || 0);
    if (sortBy === "service") return (a.service || "").localeCompare(b.service || "");
    return (a.account_id || "").localeCompare(b.account_id || "");
  });

  const hasActiveFilters = filters.accountIds.length > 0 || filters.services.length > 0 || filters.regions.length > 0;

  return (
    <div className="animate-fadeIn">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-white">Resource Explorer</h1>
      </div>

      {/* Shared Filter Panel */}
      <div className="mb-4">
        <FilterPanel />
      </div>

      {/* Sort + count bar */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-gray-400">
          {sorted.length} resources{hasActiveFilters ? " (filtered)" : ""}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-500 uppercase">Sort:</span>
          {(["cost", "service", "account"] as const).map(s => (
            <button key={s} onClick={() => setSortBy(s)}
              className={`px-2 py-1 rounded text-[10px] border transition-all ${sortBy === s ? "bg-blue-500/20 text-blue-400 border-blue-500/30" : "border-[#2a2d3e] text-gray-500 hover:text-gray-300"}`}>
              {s === "cost" ? "Cost ↓" : s === "service" ? "Service" : "Account"}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#1e2130] border border-[#2a2d3e] rounded-xl overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading resources...</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#1a1d27]">
                <th className="text-left px-4 py-3 text-xs text-gray-300 uppercase tracking-wider font-semibold">Name</th>
                <th className="text-left px-4 py-3 text-xs text-gray-300 uppercase tracking-wider font-semibold">Resource ID</th>
                <th className="text-left px-4 py-3 text-xs text-gray-300 uppercase tracking-wider font-semibold">Service</th>
                <th className="text-left px-4 py-3 text-xs text-gray-300 uppercase tracking-wider font-semibold">Account</th>
                <th className="text-left px-4 py-3 text-xs text-gray-300 uppercase tracking-wider font-semibold">Usage Type</th>
                <th className="text-right px-4 py-3 text-xs text-gray-300 uppercase tracking-wider font-semibold">Cost (USD)</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => {
                const name = extractName(r.resource_id, r.service);
                const shortRid = r.resource_id?.length > 50 ? "..." + r.resource_id.slice(-45) : r.resource_id;
                const utype = (r.usage_type || "").replace(/^[A-Z]{3}\d?-/, "");
                return (
                  <tr key={i} className="border-t border-[#2a2d3e] hover:bg-[#252840] transition-colors">
                    <td className="px-4 py-2.5 font-medium">{name}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500" title={r.resource_id}>{shortRid}</td>
                    <td className="px-4 py-2.5">{shortSvc(r.service)}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-400">{r.account_id || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-400">{utype}</td>
                    <td className="px-4 py-2.5 text-right font-semibold tabular-nums">{fmt(r.cost)}</td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No resources found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
